import { createFileRoute } from "@tanstack/react-router";
import bookCoverAi from "@/assets/book-cover-ai.jpg";
import bookCoverClimate from "@/assets/book-cover-climate.jpg";
import bookCoverLoneliness from "@/assets/book-cover-loneliness.jpg";
import { hreflangLinks } from "@/lib/seo";
import { breadcrumbSchema, personSchema, EN_HOME } from "@/lib/en-schema";
import { enSocialMeta, truncate } from "@/lib/en-meta";
import { pageTitle } from "@/lib/brand";
import { BOOK_TITLES } from "@/content/books";

const CANONICAL = "https://smartsustainability.ai/books";
const TITLE = pageTitle("Books by Arda Öztaşkın", "en");
const DESCRIPTION = truncate(
  "Three books by Arda Öztaşkın on AI, climate, and the loneliness economy — the long-form counterpart to the essays and concepts on this site.",
);

const books = [
  {
    cover: bookCoverLoneliness,
    title: BOOK_TITLES.loneliness.titleEn,
    original: "Yalnızlık A.Ş.",
    year: "2026",
    publisher: "Independent",
    summary:
      "How loneliness became a market — and what a society organised around it forgets about being human. A short book on the price of connection sold back to us as a product.",
    amazon: "https://www.amazon.com/dp/B0H59HPG62",
  },
  {
    cover: bookCoverAi,
    title: BOOK_TITLES.ai.titleEn,
    original: "Yapay Zekâ: Gücün İki Yüzü",
    year: "2024",
    publisher: "Destek Yayınları",
    summary:
      "A field guide to AI along the axis of promise and risk. Written for readers who need a working vocabulary rather than a hype cycle — practical framing for individuals, boards, and institutions.",
    amazon: "https://www.amazon.com/dp/B0H58HHS3B",
  },
  {
    cover: bookCoverClimate,
    title: BOOK_TITLES.climate.titleEn,
    original: "İklim Değil, İnsanlık Krizi",
    year: "2023",
    publisher: "Yeni İnsan Yayınevi",
    summary:
      "A reading of the climate crisis as a crisis of the human — a case for personal responsibility inside systemic change, and for the language a serious environmental politics still lacks.",
    amazon: "https://www.amazon.com/dp/B0H598BYTV",
  },
];

export const Route = createFileRoute("/books")({
  head: () => ({
    meta: [
      { title: TITLE },
      ...enSocialMeta({ title: TITLE, description: DESCRIPTION, url: CANONICAL, type: "website" }),
    ],
    links: [
      { rel: "canonical", href: CANONICAL },
      ...hreflangLinks("/kitaplar", "/books"),
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(personSchema()) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbSchema([
            { name: "Home", item: EN_HOME },
            { name: "Books", item: CANONICAL },
          ]),
        ),
      },
    ],
  }),
  component: BooksPage,
});

const serif = "font-['Fraunces',Georgia,serif]";

function BooksPage() {
  return (
    <article className="mx-auto max-w-5xl px-6 py-16 md:py-24 space-y-14">
      <header className="max-w-3xl space-y-5">
        <p className="text-clay text-[10px] uppercase tracking-[0.28em] font-semibold">Books</p>
        <h1 className={`${serif} text-4xl md:text-6xl leading-[1.05] tracking-tight text-ink text-balance`}>
          Books by Arda Öztaşkın
        </h1>
        <p className={`${serif} text-lg md:text-xl text-ink/80 leading-relaxed`}>
          The long-form counterpart to the essays and concepts on this site — on
          AI, climate, and the loneliness economy. Each book is available in
          Turkish; English translations are in progress.
        </p>
      </header>

      <section className="grid sm:grid-cols-3 gap-10 border-t border-border pt-12">
        {books.map((b) => (
          <a
            key={b.title}
            href={b.amazon}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex flex-col gap-3"
          >
            <div className="overflow-hidden bg-ink/5 aspect-[2/3]">
              <img
                src={b.cover}
                alt={`${b.title} book cover`}
                width={300}
                height={450}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover shadow-xl transition-transform duration-500 group-hover:scale-[1.03]"
              />
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-ink/60">
              {b.year} · {b.publisher}
            </div>
            <h2 className={`${serif} text-xl leading-snug text-ink group-hover:text-clay transition-colors`}>
              {b.title}
            </h2>
            <p className="text-[13px] text-ink/60 italic -mt-1">
              Original title: {b.original}
            </p>
            <p className="text-[14px] leading-relaxed text-ink/75">{b.summary}</p>
            <span className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] font-bold text-clay border-b border-clay/40 group-hover:border-clay pb-0.5 self-start">
              Find on Amazon →
            </span>
          </a>
        ))}
      </section>
    </article>
  );
}
